import { useState, useMemo } from 'react';
import { format, startOfMonth, addMonths, parseISO } from 'date-fns';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { TrendingUp, Users, DollarSign, Percent, Calendar } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { FinanceNav, MoneyDisplay, BusinessUnitSelector, BrandSelector } from '@/components/finance';
import { useCohortAnalysis, type CohortMode } from '@/hooks/finance/useCohortAnalysis';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import type { Brand } from '@/components/finance/BrandSelector';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Color scale for heatmap (green gradient)
function getHeatmapColor(value: number, max: number): string {
  if (value === 0 || max === 0) return 'transparent';
  const intensity = Math.min(value / max, 1);
  const alpha = 0.1 + intensity * 0.6;
  return `hsla(var(--primary), ${alpha})`;
}

// Gerar opções de mês/ano (24 meses passados + 24 meses futuros)
function generateMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  
  // 24 meses no passado até 24 meses no futuro (ordenado do mais antigo ao mais recente)
  for (let i = 24; i >= -24; i--) {
    const date = addMonths(startOfMonth(now), -i);
    options.push({
      value: format(date, 'yyyy-MM'),
      label: format(date, 'MMM/yyyy'),
    });
  }
  
  return options;
}

// Default period: últimos 18 meses
function getDefaultPeriod(): { start: string; end: string } {
  const now = new Date();
  const end = format(startOfMonth(now), 'yyyy-MM');
  const start = format(addMonths(startOfMonth(now), -17), 'yyyy-MM');
  return { start, end };
}

const HORIZON_OPTIONS = [
  { value: '12', label: '12 meses' },
  { value: '24', label: '24 meses' },
  { value: '36', label: '36 meses' },
];

export default function CohortAnalysisReport() {
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>('all');
  const [brand, setBrand] = useState<Brand>('all');
  const [mode, setMode] = useState<CohortMode>('received');
  const [horizon, setHorizon] = useState('24');
  
  const defaultPeriod = getDefaultPeriod();
  const [cohortStartMonth, setCohortStartMonth] = useState(defaultPeriod.start);
  const [cohortEndMonth, setCohortEndMonth] = useState(defaultPeriod.end);

  const monthOptions = useMemo(() => generateMonthOptions(), []);

  // Validar período (start <= end)
  const isValidPeriod = cohortStartMonth <= cohortEndMonth;

  const { data, isLoading, error } = useCohortAnalysis({
    businessUnit,
    brand,
    mode,
    cohortStartMonth,
    cohortEndMonth,
    maxRelativeMonths: parseInt(horizon, 10),
  });

  // Calculate max value for heatmap
  const maxMonthlyValue = useMemo(() => {
    if (!data?.cohorts) return 0;
    let max = 0;
    data.cohorts.forEach(cohort => {
      Object.values(cohort.monthlyData).forEach(value => {
        max = Math.max(max, value);
      });
    });
    return max;
  }, [data?.cohorts]);

  // Prepare stacked bar chart data
  const stackedChartData = useMemo(() => {
    if (!data?.cohorts) return [];

    return data.cohorts.map(cohort => {
      const entry: Record<string, string | number> = {
        cohort: cohort.cohortLabel,
      };
      
      for (let m = 0; m <= data.maxRelativeMonth; m++) {
        entry[`M${m}`] = cohort.monthlyData[m] || 0;
      }
      
      return entry;
    });
  }, [data]);

  // Colors for bars
  const barColors = [
    'hsl(var(--primary))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
    'hsl(142, 76%, 36%)',
    'hsl(221, 83%, 53%)',
    'hsl(262, 83%, 58%)',
    'hsl(24, 95%, 53%)',
    'hsl(346, 77%, 50%)',
    'hsl(199, 89%, 48%)',
    'hsl(47, 95%, 53%)',
    'hsl(173, 80%, 40%)',
  ];

  // Textos dinâmicos baseados no modo
  const modeTexts = {
    received: {
      title: 'Análise de Cohort - Recebido',
      description: 'Quanto foi recebido por mês dos contratos fechados em cada período',
      matrixDesc: 'Valores recebidos por mês relativo ao fechamento do contrato (M0 = mês do fechamento)',
      totalLabel: 'Total Recebido',
      percentLabel: '% Recebido',
      curveTitle: 'Curva de Recebimento',
      curveDesc: 'Média de % recebido acumulado por mês relativo',
    },
    scheduled: {
      title: 'Análise de Cohort - Previsto',
      description: 'Parcelas em aberto por mês de vencimento dos contratos fechados em cada período',
      matrixDesc: 'Valores em aberto por mês de vencimento relativo ao fechamento (M0 = mês do fechamento)',
      totalLabel: 'Total em Aberto',
      percentLabel: '% em Aberto',
      curveTitle: 'Curva de Vencimentos',
      curveDesc: 'Média de % em aberto acumulado por mês relativo',
    },
  };

  const texts = modeTexts[mode];

  if (error) {
    return (
      <div className="space-y-6">
        <FinanceNav />
        <div className="text-destructive">Erro ao carregar dados: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FinanceNav />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">{texts.title}</h1>
      </div>
      
      <div className="flex flex-col gap-4">

        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-4">
          {/* Modo */}
          <div className="space-y-1.5">
            <Label>Modo</Label>
            <Tabs value={mode} onValueChange={(v) => setMode(v as CohortMode)}>
              <TabsList>
                <TabsTrigger value="received">Recebido</TabsTrigger>
                <TabsTrigger value="scheduled">Previsto</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Período */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Cohort de
            </Label>
            <Select value={cohortStartMonth} onValueChange={setCohortStartMonth}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>até</Label>
            <Select value={cohortEndMonth} onValueChange={setCohortEndMonth}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Horizonte */}
          <div className="space-y-1.5">
            <Label>Horizonte</Label>
            <Select value={horizon} onValueChange={setHorizon}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HORIZON_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Unidade e Marca */}
          <div className="space-y-1.5">
            <Label>Unidade</Label>
            <BusinessUnitSelector value={businessUnit} onChange={setBusinessUnit} />
          </div>
          <div className="space-y-1.5">
            <Label>Marca</Label>
            <BrandSelector value={brand} onChange={setBrand} />
          </div>

          {/* Aviso de período inválido */}
          {!isValidPeriod && (
            <div className="self-end px-3 py-1.5 bg-destructive/10 text-destructive text-sm rounded-full">
              Período inválido (início {">"} fim)
            </div>
          )}
        </div>

        {/* Hint para modo Previsto */}
        {mode === 'scheduled' && (
          <p className="text-sm text-muted-foreground">
            No modo Previsto, M0/M1/M2… são meses relativos ao fechamento do deal — parcelas de 2026+ aparecem nas colunas correspondentes.
          </p>
        )}
      </div>

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Contratos</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="text-2xl font-bold">{data?.summary.totalDeals || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Total Contratos</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <div className="text-2xl font-bold">
                <MoneyDisplay value={data?.summary.totalContractValue || 0} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{texts.totalLabel}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <div className="text-2xl font-bold text-primary">
                <MoneyDisplay value={data?.summary.totalReceived || 0} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{texts.percentLabel}</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">
                {(data?.summary.avgPercentReceived || 0).toFixed(1)}%
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cohort Matrix */}
      <Card>
        <CardHeader>
          <CardTitle>Matriz de Cohort</CardTitle>
          <CardDescription>{texts.matrixDesc}</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : !data?.cohorts.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">Nenhum dado encontrado</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Não há deals fechados no período selecionado ou você não tem permissão para visualizar os dados.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-background z-10">Cohort</TableHead>
                  <TableHead className="text-right">Deals</TableHead>
                  {Array.from({ length: (data?.maxRelativeMonth || 0) + 1 }, (_, i) => (
                    <TableHead key={i} className="text-right min-w-[80px]">
                      M{i}
                    </TableHead>
                  ))}
                  <TableHead className="text-right font-bold">Total</TableHead>
                  <TableHead className="text-right">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.cohorts.map((cohort) => (
                  <TableRow key={cohort.cohortMonth}>
                    <TableCell className="sticky left-0 bg-background z-10 font-medium">
                      {cohort.cohortLabel}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {cohort.totalDeals}
                    </TableCell>
                    {Array.from({ length: (data?.maxRelativeMonth || 0) + 1 }, (_, i) => {
                      const value = cohort.monthlyData[i] || 0;
                      return (
                        <TableCell 
                          key={i} 
                          className="text-right"
                          style={{ 
                            backgroundColor: getHeatmapColor(value, maxMonthlyValue),
                          }}
                        >
                          {value > 0 ? (
                            <span className="text-xs">
                              {(value / 1000).toFixed(1)}k
                            </span>
                          ) : (
                            <span className="text-muted-foreground/50">-</span>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right font-semibold">
                      ${(cohort.totalReceived / 1000).toFixed(1)}k
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn(
                        "text-xs font-medium",
                        cohort.percentReceived >= 80 && "text-green-600",
                        cohort.percentReceived >= 50 && cohort.percentReceived < 80 && "text-yellow-600",
                        cohort.percentReceived < 50 && "text-orange-600"
                      )}>
                        {cohort.percentReceived.toFixed(0)}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Stacked Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Composição por Cohort</CardTitle>
            <CardDescription>
              Distribuição por mês relativo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[350px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={stackedChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="cohort" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value: number, name: string) => [
                      `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                      name
                    ]}
                  />
                  <Legend />
                  {Array.from({ length: (data?.maxRelativeMonth || 0) + 1 }, (_, i) => (
                    <Bar 
                      key={i}
                      dataKey={`M${i}`} 
                      stackId="a" 
                      fill={barColors[i % barColors.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Retention Curve */}
        <Card>
          <CardHeader>
            <CardTitle>{texts.curveTitle}</CardTitle>
            <CardDescription>{texts.curveDesc}</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[350px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={data?.retentionCurve} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="label" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => `${value.toFixed(0)}%`}
                    domain={[0, 'auto']}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value: number, name: string) => [
                      `${value.toFixed(1)}%`,
                      name === 'cumulativePercent' ? 'Acumulado' : 'No Mês'
                    ]}
                  />
                  <Legend 
                    formatter={(value) => value === 'cumulativePercent' ? 'Acumulado' : 'No Mês'}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avgPercent" 
                    stroke="hsl(var(--chart-2))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--chart-2))' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cumulativePercent" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={3}
                    dot={{ fill: 'hsl(var(--primary))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
